'use client';

import { useEffect, useRef } from 'react';
import type { Criterion, CriteriaOp } from '@/domain/search-criteria';
import { requestRefresh } from '@/lib/refresh-gate';
import { catchUpReaders, pushToStore, surfaceCursor, type LiveRecord } from './live-store';
import { liveCloseOutcome } from './live-close';
import { replayQueue } from './live-queue';

/**
 * The client half of the sync engine (docs/SYNC.md §5.3, §5.5): one websocket
 * per tab, declarations from the live surfaces, and the reaction to a tick.
 * Slice 1's reaction is a refresh of the current route, debounced and asked of
 * the refresh coordinator (`requestRefresh`, src/lib/refresh-gate.ts); slice 2
 * replaces it with pushed rows into the per-tab store.
 *
 * The coordinator, not this file, decides when the refresh starts (2026-09-28):
 * it holds the request while a board drag settles, while a click's navigation
 * is in flight, and while one of the tab's own writes or refreshes is in
 * flight, and runs every request that waited as one refresh once that lands.
 * The tick of the viewer's own write arrives while the write's action is still
 * streaming its refreshed tree; started then, the tick's refresh replaced that
 * tree and the write's result reached the screen seconds late.
 *
 * `LiveConnection` mounts once in the app shell and owns the socket.
 * `useLiveInterest` / `LiveInterest` are what a surface uses to say what it
 * renders; the declaration is re-sent on every reconnect, and retracted on
 * unmount. A reconnect after a drop refreshes once, because the tab cannot
 * know what it missed (the cursor arrives with slice 2).
 */

export interface LiveDeclaration {
  ids: string[];
  roots?: string[];
  /**
   * The board or list the surface draws, beside its root (docs/SYNC.md §5.3).
   * The server routes that view's root for a viewer who may read the view,
   * even one who cannot open the root: a member of a shared list's audience.
   */
  view?: string | null;
  query?: { criteria: Criterion[]; op: CriteriaOp; showClosed: boolean } | null;
  org?: boolean;
  /**
   * The log position the page's DATA was read at (`logHorizon()`), for the race
   * in docs/SYNC.md §5.6.
   *
   * **Take it before the page's content reads, never after** (2026-09-14). The
   * server answers the subscribe race by comparing this against what it has
   * routed since: a watermark at or past the head proves nothing was missed and
   * sends no tick. Stamped AFTER the reads it claims a freshness the rendered
   * rows do not have - a write that commits between the read and the stamp is
   * inside the watermark, so the catch-up stays silent and the stale page sits
   * there until something else happens to move. Stamped first it can only ever
   * be too OLD, which costs one extra refresh.
   */
  wm?: number | null;
}

type ServerMessage =
  | { t: 'hello' }
  | { t: 'tick'; s: string[] }
  | { t: 'push'; s: string; rows: LiveRecord[]; dropped: string[]; cur?: string }
  | { t: 'synced'; s: string };

const REFRESH_DEBOUNCE_MS = 300;
const BACKOFF_MIN_MS = 1000;
const BACKOFF_MAX_MS = 30_000;
/** How long a cursor-carrying reconnect waits for the server's replay before it
 *  falls back to the blind refresh of slices 1 and 2. */
const REPLAY_FUSE_MS = 1000;

class LiveClient {
  private ws: WebSocket | null = null;
  private readonly decls = new Map<string, LiveDeclaration>();
  private onTick: (() => void) | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private backoff = BACKOFF_MIN_MS;
  private everOpened = false;
  private replayFuse: ReturnType<typeof setTimeout> | null = null;
  private stopped = true;

  start(onTick: () => void): void {
    this.onTick = onTick;
    this.stopped = false;
    this.connect();
  }

  stop(): void {
    this.stopped = true;
    this.onTick = null;
    this.defuseReplayFuse();
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this.ws?.close();
    this.ws = null;
  }

  declare(key: string, decl: LiveDeclaration): void {
    this.decls.set(key, decl);
    this.send({ t: 'sub', s: key, ...toWire(decl) });
  }

  retract(key: string): void {
    this.decls.delete(key);
    this.send({ t: 'unsub', s: key });
  }

  private send(message: Record<string, unknown>): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(message));
  }

  private connect(): void {
    if (this.stopped || this.ws) return;
    const scheme = location.protocol === 'https:' ? 'wss' : 'ws';
    let ws: WebSocket;
    try {
      ws = new WebSocket(`${scheme}://${location.host}/rt`);
    } catch {
      this.scheduleRetry();
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      this.backoff = BACKOFF_MIN_MS;
      // Re-declare everything the tab renders. Watermarks are stale after a
      // drop; the CURSOR is not (docs/SYNC.md §5.6, slice 3): a re-connect hands
      // back the last cursor it holds per surface and the server replays the gap
      // as pushes, or ticks when the gap is past the horizon. A surface with no
      // cursor yet (nothing was ever pushed to it) still needs the blind
      // refresh, so the tab refreshes once only when one of them has none.
      const reconnect = this.everOpened;
      this.everOpened = true;
      let blind = false;
      for (const [key, decl] of this.decls) {
        const cur = reconnect ? surfaceCursor(key) : null;
        if (reconnect && !cur) blind = true;
        this.send({
          t: 'sub',
          s: key,
          ...toWire(reconnect ? { ...decl, wm: null } : decl),
          ...(cur ? { cur } : {}),
        });
      }
      if (reconnect && blind) this.onTick?.();
      // A cursor is only worth trusting once the gap actually comes back. A
      // server that does not read `cur` answers nothing, and the tab would sit
      // on a stale surface until the next event - a push a tab never received is
      // indistinguishable from one that never happened. So a cursor-carrying
      // reconnect arms the old blind refresh on a short fuse, and the server's
      // first message defuses it: with the replay in place this costs nothing,
      // without it nothing is lost.
      else if (reconnect) this.armReplayFuse();
      // A socket that just came back is the moment the deferred acts go out
      // (docs/SYNC.md §7.4: replayed in device order on reconnect).
      void replayQueue();
    };
    ws.onmessage = (e) => {
      let msg: ServerMessage;
      try {
        msg = JSON.parse(String(e.data)) as ServerMessage;
      } catch {
        return;
      }
      // Anything but the open handshake proves the server is answering this
      // reconnect, so the fuse above is spent.
      if (msg.t !== 'hello') this.defuseReplayFuse();
      // A tick refreshes (slice 1); hello is the open handshake. A push patches
      // the per-tab store and does NOT refresh (slice 2, docs/SYNC.md §5.5) -
      // BUT only the lens list rail consumes the store this slice, so a push to
      // a surface with no live reader (a board or saved-list page) falls back to
      // the tick refresh, or that surface would stop updating.
      // `synced` is the replay's empty answer: the gap held nothing for that
      // surface. It needs no reaction beyond the defuse above - which is the
      // whole point of sending it (docs/SYNC.md §5.6).
      if (msg.t === 'tick') this.onTick?.();
      else if (msg.t === 'push') {
        if (!pushToStore(msg.s, msg.rows, msg.dropped, msg.cur)) this.onTick?.();
      }
    };
    ws.onclose = (event) => {
      if (this.ws === ws) this.ws = null;
      if (liveCloseOutcome(event.code) === 'session-ended') {
        // The session ended (a sign-out, a revoked session, a deleted
        // account): another socket would be refused the same way, so no
        // reconnect. One refresh lets the page find out (./live-close.ts).
        this.stopped = true;
        this.defuseReplayFuse();
        this.onTick?.();
        return;
      }
      this.scheduleRetry();
    };
    ws.onerror = () => {
      ws.close();
    };
  }

  private scheduleRetry(): void {
    if (this.stopped || this.retryTimer) return;
    const delay = this.backoff;
    this.backoff = Math.min(BACKOFF_MAX_MS, this.backoff * 2);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.connect();
    }, delay);
  }

  /** Arm the reconnect's fallback refresh: fired unless the server replays the
   *  gap first (docs/SYNC.md §5.6). */
  private armReplayFuse(): void {
    if (this.replayFuse) clearTimeout(this.replayFuse);
    this.replayFuse = setTimeout(() => {
      this.replayFuse = null;
      this.onTick?.();
    }, REPLAY_FUSE_MS);
  }

  private defuseReplayFuse(): void {
    if (!this.replayFuse) return;
    clearTimeout(this.replayFuse);
    this.replayFuse = null;
  }

  /** A hidden tab that lost its socket reconnects when it comes back. */
  nudge(): void {
    if (this.stopped || this.ws) return;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this.backoff = BACKOFF_MIN_MS;
    this.connect();
  }
}

function toWire(decl: LiveDeclaration): Record<string, unknown> {
  return {
    ids: decl.ids,
    roots: decl.roots ?? [],
    view: decl.view ?? null,
    q: decl.query ?? null,
    org: decl.org ?? false,
    wm: decl.wm ?? null,
  };
}

const client = new LiveClient();

/** Mounted once in the app shell: owns the socket and the refresh reaction. Renders nothing. */
export function LiveConnection() {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const refresh = () => {
      // A tick says only "something moved". The reader surfaces read their
      // own records again first, so a line someone else just closed is snapshotted
      // before the refresh hides it (docs/SYNC.md §7.3; the store's
      // `catchUpReaders` says why). It runs beside the refresh, not before it:
      // the refresh must never wait on it.
      void catchUpReaders();
      if (timer.current) clearTimeout(timer.current);
      // The coordinator holds it while a board drag settles, while a click's
      // navigation or one of this tab's writes or refreshes is in flight
      // (src/lib/refresh-gate.ts), and requests that waited coalesce.
      timer.current = setTimeout(() => requestRefresh('live'), REFRESH_DEBOUNCE_MS);
    };
    client.start(refresh);
    const onVisible = () => {
      if (document.visibilityState === 'visible') client.nudge();
    };
    // The navigation hold's click listener lives with the coordinator's runner
    // now (src/components/refresh-host.tsx), so it covers every page.
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      client.stop();
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  return null;
}

/** Declare what a surface renders while it is mounted; `null` declares nothing. */
export function useLiveInterest(key: string, decl: LiveDeclaration | null): void {
  const json = decl ? JSON.stringify(decl) : null;
  useEffect(() => {
    if (json === null) return;
    client.declare(key, JSON.parse(json) as LiveDeclaration);
    return () => client.retract(key);
  }, [key, json]);
}

/** The declaration as a component, for server components to place beside a surface. */
export function LiveInterest({ surface, ...decl }: LiveDeclaration & { surface: string }) {
  useLiveInterest(surface, decl);
  return null;
}
